import type { UploadType } from '@/api';
import { getApiClient } from '@/api/client';
import type { NormalizedError } from '@/utils/error-utils';
import { normalizeApiError } from '@/utils/error-utils';
import { getEnvironmentLabel } from '@/utils/get-environment';
import { useMutation, useQuery } from '@tanstack/react-query';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';

export const USER_PERMISSIONS_QUERY_KEY = ['userPermissions'] as const;

export const useUserPermissions = (
  accountId: string | undefined,
  roles: string[],
  enabled = true,
) =>
  useQuery({
    queryKey: [...USER_PERMISSIONS_QUERY_KEY, accountId, [...roles].sort()],
    queryFn: () =>
      getApiClient()
        .userMePermissions()
        .then((res) => res.permissions),
    enabled: enabled && Boolean(accountId),
    staleTime: 5 * 60 * 1000,
  });

export const useFlowhavenUpdate = (
  onSuccess: (numberOfUpdatedPrices: number) => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: () => getApiClient().loadLelRrpPrices(),
    onSuccess: async (response) =>
      onSuccess(response.numberOfUpdatedProductPlans),
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useTriggerAutoCalc = (
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: () => getApiClient().triggerAutoCalc(),
    onSuccess: () => onSuccess(),
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useGetTemplate = (target: string) =>
  useQuery({
    queryKey: ['getTemplate', target],
    queryFn: async () => getApiClient().getTemplate({ target }),
    enabled: false,
    retry: false,
  });

export const useGetPresignedPutUrl = (target: string) =>
  useMutation({
    mutationFn: (fileName: string) => {
      return getApiClient()
        .getPresignedPutUrl({
          createPresignedUrlRequest: {
            target,
            fileName,
          },
        })
        .then((response) => ({
          uploadUrl: response.uploadUrl,
          logId: response.logId,
          target,
        }));
    },
  });

export type UploadHandle = {
  promise: Promise<void>;
  cancel: () => void;
};

export const uploadFileToPresignedUrl = ({
  file,
  uploadUrl,
  logId,
  target,
  onProgress,
}: {
  file: File;
  uploadUrl: string;
  logId: string;
  target: string;
  onProgress?: (progress: number) => void;
}): UploadHandle => {
  const env = getEnvironmentLabel();

  const taggingHeader = new URLSearchParams({
    LogId: logId,
    Target: target,
    Env: env,
  }).toString();

  const uppy = new Uppy({ restrictions: { maxNumberOfFiles: 1 } });

  uppy.use(AwsS3, {
    shouldUseMultipart: false,
    getUploadParameters: () => ({
      method: 'PUT',
      url: uploadUrl,
      headers: { 'x-amz-tagging': taggingHeader },
    }),
  });

  if (onProgress) {
    uppy.on('progress', onProgress);
  }

  uppy.addFile({ name: file.name, type: file.type, data: file });

  let destroyed = false;

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    if (onProgress) {
      uppy.off('progress', onProgress);
    }
    uppy.destroy();
  };

  const promise = uppy
    .upload()
    .then((result) => {
      const failed = result?.failed ?? [];
      if (failed.length > 0) {
        throw new Error(failed[0]?.error ?? 'S3 upload failed');
      }
    })
    .finally(destroy);

  const cancel = () => {
    uppy.cancelAll();
    destroy();
  };

  return { promise, cancel };
};

export const useGetUploadReport = (type: UploadType) =>
  useMutation({
    mutationFn: async (fileId: string) =>
      getApiClient()
        .process({ logId: fileId, type: type })
        .then((response) => response),
  });

export const usePriceEventQueueStatus = () =>
  useQuery({
    queryKey: ['priceEventQueueStatus'],
    queryFn: async () => getApiClient().getPriceEventQueueStatus(),
    refetchInterval: 60_000, // Refetch every 60 seconds
    refetchIntervalInBackground: false,
  });
